import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Autocomplete,
  Avatar,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ImagePicker from '../components/ImagePicker';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

interface Product {
  productId: number;
  name: string;
  imageUrl: string;
}

interface Carousel {
  productsCarouselId: number;
  image: string;
  imageBigger: string;
}

function SortableProductRow({
  product,
  onEdit,
  onDelete,
}: {
  product: Product;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: product.productId });

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <TableCell sx={{ width: 40 }}>
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          sx={{ cursor: 'grab' }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell>
        <img
          src={`${IMAGE_URL}${product.imageUrl}`}
          alt={product.name}
          width={100}
        />
      </TableCell>
      <TableCell>{product.name}</TableCell>
      <TableCell align="right">
        <IconButton onClick={onEdit}>
          <EditIcon />
        </IconButton>
        <IconButton onClick={onDelete}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

const CarouselEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [carousel, setCarousel] = React.useState<Carousel | null>(null);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(true);

  const [image, setImage] = React.useState<File | null>(null);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [mediaId, setMediaId] = React.useState<number | null>(null);

  const [imageBigger, setImageBigger] = React.useState<File | null>(null);
  const [imageBiggerUrl, setImageBiggerUrl] = React.useState<string | null>(
    null,
  );
  const [mediaBiggerId, setMediaBiggerId] = React.useState<number | null>(null);

  const [searchOptions, setSearchOptions] = React.useState<Product[]>([]);
  const [searchInput, setSearchInput] = React.useState('');
  const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(
    null,
  );
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [adding, setAdding] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/carousel/${id}`)
      .then((res) => {
        setCarousel(res.data.carousel);
        setProducts(res.data.products);
        if (res.data.carousel.image)
          setImageUrl(`${IMAGE_URL}/${res.data.carousel.image}`);
        if (res.data.carousel.imageBigger)
          setImageBiggerUrl(`${IMAGE_URL}/${res.data.carousel.imageBigger}`);
      })
      .catch(() => alert('Не вдалося завантажити каталог'))
      .finally(() => setLoading(false));
  }, [id]);

  React.useEffect(() => {
    if (!searchInput.trim()) {
      setSearchOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      setSearchLoading(true);
      axios
        .get(`${API_URL}/products`, {
          params: { search: searchInput, limit: 20 },
        })
        .then((res) => setSearchOptions(res.data.data))
        .catch(() => setSearchOptions([]))
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const alreadyInCarousel = selectedProduct
    ? products.some((p) => p.productId === selectedProduct.productId)
    : false;

  const handleAddProduct = async () => {
    if (!selectedProduct || !carousel) return;
    setAdding(true);
    try {
      await axios.post(
        `${API_URL}/carousel/${carousel.productsCarouselId}/products`,
        {
          productId: selectedProduct.productId,
        },
      );
      setProducts((prev) => [...prev, selectedProduct]);
      setSelectedProduct(null);
      setSearchInput('');
    } catch {
      alert('Помилка при додаванні продукту');
    } finally {
      setAdding(false);
    }
  };

  const handleSave = async () => {
    try {
      const formData = new FormData();
      if (image) formData.append('image', image);
      if (mediaId) formData.append('mediaId', String(mediaId));
      if (imageBigger) formData.append('imageBigger', imageBigger);
      if (mediaBiggerId)
        formData.append('mediaBiggerId', String(mediaBiggerId));
      await axios.put(
        `${API_URL}/carousel/${carousel?.productsCarouselId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      navigate('/carousele');
    } catch {
      alert('Помилка при збереженні каталогу');
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!confirm('Видалити продукт з каталогу?')) return;
    try {
      await axios.delete(
        `${API_URL}/carousel/${carousel?.productsCarouselId}/products/${productId}`,
      );
      setProducts((prev) => prev.filter((p) => p.productId !== productId));
    } catch {
      alert('Помилка при видаленні продукту з каталогу');
    }
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !carousel) return;

    const oldIndex = products.findIndex((p) => p.productId === active.id);
    const newIndex = products.findIndex((p) => p.productId === over.id);

    const newProducts = arrayMove(products, oldIndex, newIndex);
    setProducts(newProducts);

    try {
      await axios.put(
        `${API_URL}/carousel/${carousel.productsCarouselId}/products/reorder`,
        {
          products: newProducts.map((p, index) => ({
            productId: p.productId,
            index,
          })),
        },
      );
    } catch {
      alert('Помилка при зміні порядку продуктів');
    }
  };

  if (loading) return <CircularProgress />;
  if (!carousel) return <Typography>Каталог не знайдений</Typography>;

  return (
    <Box sx={{ mx: 10, mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Редагування каталогу #{carousel.productsCarouselId}
      </Typography>

      <ImagePicker
        title="Зображення каталогу"
        maxDimension={1500}
        recommendedSize={1500}
        value={imageUrl || ''}
        onChange={(value) => {
          if (value.type === 'file') {
            setImage(value.file);
            setMediaId(null);
            setImageUrl(URL.createObjectURL(value.file));
          }
          if (value.type === 'media') {
            setImage(null);
            setMediaId(value.mediaId);
            setImageUrl(value.url);
          }
        }}
      />

      <ImagePicker
        title="Зображення для більших цін"
        maxDimension={1500}
        recommendedSize={1500}
        value={imageBiggerUrl || ''}
        onChange={(value) => {
          if (value.type === 'file') {
            setImageBigger(value.file);
            setMediaBiggerId(null);
            setImageBiggerUrl(URL.createObjectURL(value.file));
          }
          if (value.type === 'media') {
            setImageBigger(null);
            setMediaBiggerId(value.mediaId);
            setImageBiggerUrl(value.url);
          }
        }}
      />

      <Button variant="contained" sx={{ mt: 2, mb: 4 }} onClick={handleSave}>
        Зберегти каталог
      </Button>

      <Typography variant="h5" gutterBottom>
        Продукти каталогу
      </Typography>

      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
        <Autocomplete
          sx={{ width: 400 }}
          options={searchOptions}
          loading={searchLoading}
          value={selectedProduct}
          inputValue={searchInput}
          onInputChange={(_, val) => setSearchInput(val)}
          onChange={(_, val) => setSelectedProduct(val)}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, val) => opt.productId === val.productId}
          renderOption={(props, opt) => (
            <Box component="li" {...props} key={opt.productId}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  src={`${IMAGE_URL}/${opt.imageUrl}`}
                  variant="rounded"
                  sx={{ width: 32, height: 32 }}
                />
                <Typography variant="body2">{opt.name}</Typography>
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Пошук продукту"
              placeholder="Введіть назву..."
            />
          )}
          noOptionsText={
            searchInput ? 'Нічого не знайдено' : 'Введіть назву для пошуку'
          }
        />

        <Stack spacing={0.5}>
          <Button
            variant="contained"
            disabled={!selectedProduct || alreadyInCarousel || adding}
            onClick={handleAddProduct}
            sx={{ height: 56 }}
          >
            Додати
          </Button>
          {alreadyInCarousel && (
            <Typography variant="caption" color="warning.main">
              Вже є в каталозі
            </Typography>
          )}
        </Stack>
      </Stack>

      {products.length > 0 ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Перетягуйте рядки, щоб змінити порядок відображення в боті
          </Typography>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Фото</TableCell>
                  <TableCell>Назва</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <SortableContext
                  items={products.map((p) => p.productId)}
                  strategy={verticalListSortingStrategy}
                >
                  {products.map((p) => (
                    <SortableProductRow
                      key={p.productId}
                      product={p}
                      onEdit={() => navigate(`/products/${p.productId}`)}
                      onDelete={() => handleDeleteProduct(p.productId)}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        </Box>
      ) : (
        <Typography>Продукти відсутні</Typography>
      )}
    </Box>
  );
};

export default CarouselEdit;
